import React, { useEffect, useLayoutEffect, useRef } from "react";

import { Row } from "@/components/ui/flex";
import { cn } from "@/lib/utils";

export type SplashTypingProps = {
  content: React.ReactNode[][];
};

export const SplashTyping: React.FC<SplashTypingProps> = ({ content }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const contentSize = useRef({ width: 0, height: 0 });

  useEffect(() => {
    const rows = contentRef.current?.children;
    if (rows) {
      let rowIndex = 0;
      let elementIndex = 0;
      let interval: ReturnType<typeof setInterval>;
      let animationStartDelay = 1000; // in ms

      const timeout = setTimeout(() => {
        interval = setInterval(() => {
          if (rowIndex >= rows.length) {
            clearInterval(interval);
            return;
          }

          const row = rows[rowIndex];
          const elements = row.children;

          if (elementIndex >= elements.length) {
            elementIndex = 0;
            rowIndex++;
            return;
          }

          const element = elements[elementIndex];
          (element as HTMLElement).classList.remove(
            "opacity-0",
            "translate-x-4",
          );
          elementIndex++;
        }, 50);
      }, animationStartDelay);

      return () => {
        clearTimeout(timeout);
        clearInterval(interval);
      };
    }
  }, [content]);

  useLayoutEffect(() => {
    // Load original content size
    if (contentRef.current) {
      contentSize.current.width = contentRef.current.offsetWidth;
      contentSize.current.height = contentRef.current.offsetHeight;
    }

    const handleResize = () => {
      if (contentRef.current && wrapperRef.current) {
        const referenceWidth = contentSize.current.width;
        const referenceHeight = contentSize.current.height;
        const wrapperWidth = wrapperRef.current.offsetWidth;
        const wrapperHeight = wrapperRef.current.offsetHeight;

        const scaleWidth = wrapperWidth / referenceWidth;
        const scaleHeight = wrapperHeight / referenceHeight;
        const scale = Math.min(scaleWidth, scaleHeight);

        contentRef.current.style.transform = `translate(-50%, -50%) scale(${scale})`;
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  });

  return (
    <div
      className="absolute top-0 right-0 bottom-0 left-0 overflow-hidden"
      ref={wrapperRef}
    >
      <div
        ref={contentRef}
        className="relative top-1/2 left-1/2 inline-block whitespace-pre"
      >
        {content.map((row, rowIndex) => (
          <Row key={rowIndex}>
            {row.map((element, elementIndex) => (
              <span
                key={elementIndex}
                className="transition-opacity-transform translate-x-4 transform opacity-0 duration-200 ease-out"
              >
                <React.Fragment>{element}</React.Fragment>
              </span>
            ))}
          </Row>
        ))}
      </div>
    </div>
  );
};

export const splitSplashBubble = (
  text: string,
  props?: { italic?: boolean },
): React.ReactNode[] => {
  return text.split("").map((char, index) => {
    const isFirst = index === 0;
    const isLast = index === text.length - 1;
    const isOnly = text.length === 1;

    const roundedClass = isOnly
      ? "rounded-2xl px-2"
      : isFirst
        ? "rounded-l-2xl pl-2"
        : isLast
          ? "rounded-r-2xl pr-2"
          : "";

    return (
      <SplashTypingTypography
        key={index}
        className={cn(
          "bg-accent text-accent-foreground font-bold",
          roundedClass,
          props?.italic ? "italic" : "",
        )}
      >
        {char}
      </SplashTypingTypography>
    );
  });
};

export const splitSplashText = (
  text: string,
  props?: {
    italic?: boolean;
    className?: string;
  },
): React.ReactNode[] => {
  return text.split("").map((char, index) => (
    <SplashTypingTypography
      key={index}
      className={cn(props?.italic ? "italic" : "", props?.className)}
    >
      {char}
    </SplashTypingTypography>
  ));
};

const SplashTypingTypography: React.FC<
  React.HTMLAttributes<HTMLSpanElement>
> = ({ children, className, ...props }) => {
  return (
    <span
      className={cn([
        "text-accent-secondary-foreground text-6xl leading-normal",
        className,
      ])}
      {...props}
    >
      {children}
    </span>
  );
};
